import type { PublicTicket } from './ticketService'
import { ticketDate, ticketTime } from './components/TicketCard/TicketCard'

/**
 * Draws the ticket as a PNG, the same design as the on-screen ticket, so it
 * can be sent as a picture. Once it's in someone's chat or photos, the QR code
 * works at the door with no signal. Everything is drawn here on the device:
 * the QR code comes from the local `qrcode` library, and nothing is uploaded.
 *
 * The QR code is the ticket link, so the image is exactly as private as the
 * link. Like the ticket, it carries no contact details.
 */

const W = 1080
const MARGIN = 40
const CARD_W = W - MARGIN * 2
const PAD = 64
const TEXT_W = CARD_W - PAD * 2
const RADIUS = 40
const BAND_H = 112
const FOOTER_H = 96
const QR_BOX = 640
const QR_PAD = 36

const gold = '#e8b84b'
const navy = '#0a1628'
const navyMid = '#112240'
const ivory = '#f2efe4'
const muted = '#7a90a8'
const green = '#6fcf97'

const sans = 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
const serif = 'Georgia, "Times New Roman", serif'
const emoji = '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif'

type Ctx = CanvasRenderingContext2D

function font(ctx: Ctx, size: number, weight: number | 'normal' = 'normal', family = sans) {
  ctx.font = `${weight} ${size}px ${family}`
}

function spacing(ctx: Ctx, px: number) {
  // Letter spacing is newer canvas API; where it's missing, text is just tighter.
  if ('letterSpacing' in ctx) (ctx as Ctx & { letterSpacing: string }).letterSpacing = `${px}px`
}

/** Breaks text into lines that fit `width`, breaking long words if they must. */
export function wrapText(ctx: Pick<Ctx, 'measureText'>, text: string, width: number): string[] {
  const lines: string[] = []
  let line = ''
  for (const word of text.split(/\s+/).filter(Boolean)) {
    const next = line ? `${line} ${word}` : word
    if (ctx.measureText(next).width <= width) {
      line = next
      continue
    }
    if (line) lines.push(line)
    // A single word wider than the line (a long address or URL) is split by characters.
    let rest = word
    while (ctx.measureText(rest).width > width && rest.length > 1) {
      let cut = rest.length - 1
      while (cut > 1 && ctx.measureText(rest.slice(0, cut)).width > width) cut -= 1
      lines.push(rest.slice(0, cut))
      rest = rest.slice(cut)
    }
    line = rest
  }
  if (line) lines.push(line)
  return lines.length ? lines : ['']
}

function roundedRect(ctx: Ctx, x: number, y: number, w: number, h: number, r: number | [number, number, number, number]) {
  const [tl, tr, br, bl] = typeof r === 'number' ? [r, r, r, r] : r
  ctx.beginPath()
  ctx.moveTo(x + tl, y)
  ctx.lineTo(x + w - tr, y)
  ctx.quadraticCurveTo(x + w, y, x + w, y + tr)
  ctx.lineTo(x + w, y + h - br)
  ctx.quadraticCurveTo(x + w, y + h, x + w - br, y + h)
  ctx.lineTo(x + bl, y + h)
  ctx.quadraticCurveTo(x, y + h, x, y + h - bl)
  ctx.lineTo(x, y + tl)
  ctx.quadraticCurveTo(x, y, x + tl, y)
  ctx.closePath()
}

/**
 * Lays the ticket out from the top. With `draw` false it only measures, so
 * the canvas can be sized to fit exactly before drawing for real.
 */
function layout(ctx: Ctx, ticket: PublicTicket, qr: CanvasImageSource | null, draw: boolean): number {
  const left = MARGIN + PAD
  let y = MARGIN + BAND_H + 56

  // Event name, in serif like the on-screen ticket.
  font(ctx, 78, 700, serif)
  spacing(ctx, 0)
  ctx.fillStyle = ivory
  ctx.textBaseline = 'alphabetic'
  for (const line of wrapText(ctx, ticket.eventName, TEXT_W)) {
    y += 78
    if (draw) ctx.fillText(line, left, y)
    y += 8
  }
  y += 36

  // Date, time and venue, each marked with its emoji.
  const time = ticketTime(ticket)
  const rows: Array<[string, string, string | null]> = [
    ['🗓️', ticketDate(ticket), null],
    ...(time ? [['🕒', time.times, time.timezone] as [string, string, string]] : []),
    ['📍', ticket.location, null],
  ]
  const textLeft = left + 72
  for (const [icon, value, extra] of rows) {
    font(ctx, 38)
    const lines = wrapText(ctx, extra ? `${value} ${extra}` : value, TEXT_W - 72)
    if (draw) {
      font(ctx, 40, 'normal', emoji)
      ctx.fillText(icon, left, y + 42)
    }
    for (const [index, line] of lines.entries()) {
      y += 46
      if (draw) {
        font(ctx, 38)
        ctx.fillStyle = ivory
        if (extra && index === lines.length - 1 && line.endsWith(extra)) {
          // The time zone sits after the times, smaller and muted.
          const main = line.slice(0, line.length - extra.length)
          ctx.fillText(main, textLeft, y)
          const mainWidth = ctx.measureText(main).width
          font(ctx, 30)
          ctx.fillStyle = muted
          ctx.fillText(extra, textLeft + mainWidth, y)
        } else {
          ctx.fillText(line, textLeft, y)
        }
      }
      y += 8
    }
    y += 22
  }
  y += 14

  // Tear line.
  if (draw) {
    ctx.save()
    ctx.strokeStyle = 'rgba(232, 184, 75, 0.35)'
    ctx.lineWidth = 3
    ctx.setLineDash([14, 10])
    ctx.beginPath()
    ctx.moveTo(left, y)
    ctx.lineTo(left + TEXT_W, y)
    ctx.stroke()
    ctx.restore()
  }
  y += 56

  // Guest, and who invited them.
  font(ctx, 26, 700)
  spacing(ctx, 3)
  ctx.fillStyle = muted
  y += 26
  if (draw) ctx.fillText('GUEST', left, y)
  spacing(ctx, 0)
  y += 18
  font(ctx, 62, 800)
  ctx.fillStyle = ivory
  for (const line of wrapText(ctx, ticket.attendeeName, TEXT_W)) {
    y += 62
    if (draw) ctx.fillText(line, left, y)
    y += 10
  }
  if (ticket.invitedBy) {
    font(ctx, 36, 600)
    ctx.fillStyle = green
    for (const line of wrapText(ctx, `Invited by ${ticket.invitedBy}`, TEXT_W)) {
      y += 40
      if (draw) ctx.fillText(line, left, y)
      y += 6
    }
  }
  y += 44

  // The QR code: large and dark on white, so it scans in poor light.
  const qrX = (W - QR_BOX) / 2
  if (draw) {
    ctx.fillStyle = '#ffffff'
    roundedRect(ctx, qrX, y, QR_BOX, QR_BOX, 32)
    ctx.fill()
    if (qr) ctx.drawImage(qr, qrX + QR_PAD, y + QR_PAD, QR_BOX - QR_PAD * 2, QR_BOX - QR_PAD * 2)
  }
  y += QR_BOX + 56

  return y + FOOTER_H + MARGIN
}

function drawFrame(ctx: Ctx, ticket: PublicTicket, height: number) {
  const cardH = height - MARGIN * 2
  ctx.fillStyle = navy
  ctx.fillRect(0, 0, W, height)

  // Card, clipped so the band and footer follow its rounded corners.
  ctx.save()
  roundedRect(ctx, MARGIN, MARGIN, CARD_W, cardH, RADIUS)
  ctx.clip()
  const background = ctx.createLinearGradient(MARGIN, MARGIN, MARGIN + CARD_W * 0.6, MARGIN + cardH)
  background.addColorStop(0, navyMid)
  background.addColorStop(1, navy)
  ctx.fillStyle = background
  ctx.fillRect(MARGIN, MARGIN, CARD_W, cardH)

  ctx.fillStyle = gold
  ctx.fillRect(MARGIN, MARGIN, CARD_W, BAND_H)
  ctx.fillStyle = 'rgba(0, 0, 0, 0.3)'
  ctx.fillRect(MARGIN, MARGIN + cardH - FOOTER_H, CARD_W, FOOTER_H)
  ctx.restore()

  roundedRect(ctx, MARGIN + 2, MARGIN + 2, CARD_W - 4, cardH - 4, RADIUS - 2)
  ctx.strokeStyle = gold
  ctx.lineWidth = 4
  ctx.stroke()

  // Band: what it is, and the reference.
  ctx.textBaseline = 'middle'
  ctx.fillStyle = navy
  font(ctx, 30, 800)
  spacing(ctx, 4)
  ctx.textAlign = 'left'
  ctx.fillText('FREE ENTRY TICKET', MARGIN + PAD, MARGIN + BAND_H / 2 + 2)
  spacing(ctx, 1)
  font(ctx, 32, 700)
  ctx.globalAlpha = 0.75
  ctx.textAlign = 'right'
  ctx.fillText(`#${ticket.reference}`, MARGIN + CARD_W - PAD, MARGIN + BAND_H / 2 + 2)
  ctx.globalAlpha = 1

  // Footer.
  spacing(ctx, 0)
  font(ctx, 30)
  ctx.fillStyle = muted
  ctx.textAlign = 'center'
  ctx.fillText('Show this code at the door for entry', W / 2, MARGIN + cardH - FOOTER_H / 2)
  ctx.textAlign = 'left'
  ctx.textBaseline = 'alphabetic'
}

async function qrCanvas(value: string) {
  const { toCanvas } = await import('qrcode')
  const canvas = document.createElement('canvas')
  await toCanvas(canvas, value, { errorCorrectionLevel: 'M', margin: 0, width: QR_BOX - QR_PAD * 2, color: { dark: '#000000', light: '#ffffff' } })
  return canvas
}

/** The ticket as a PNG image, ready to share. */
export async function drawTicketImage(ticket: PublicTicket, ticketUrl: string): Promise<Blob> {
  // Wait for the page's fonts so the image uses the same ones.
  await document.fonts?.ready.catch(() => undefined)
  const qr = await qrCanvas(ticketUrl)

  const canvas = document.createElement('canvas')
  const measure = canvas.getContext('2d')
  if (!measure) throw new Error('Canvas is not available on this device.')
  const height = Math.ceil(layout(measure, ticket, qr, false))

  canvas.width = W
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas is not available on this device.')
  drawFrame(ctx, ticket, height)
  layout(ctx, ticket, qr, true)

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('The ticket image could not be made.'))), 'image/png')
  })
}

/** A friendly file name, such as ticket-madrid-outreach-ada-lovelace.png. */
export function ticketImageName(ticket: Pick<PublicTicket, 'eventName' | 'attendeeName'>) {
  const slug = (value: string) => value.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
  return `ticket-${[slug(ticket.eventName), slug(ticket.attendeeName)].filter(Boolean).join('-') || 'event'}.png`
}
